import {
  ChangeDetectorRef,
  Component,
  Input,
  forwardRef,
  ChangeDetectionStrategy,
} from '@angular/core';
import {
  ControlValueAccessor,
  FormGroup,
  NG_VALUE_ACCESSOR,
} from '@angular/forms';

import { LoggerService } from '@services/logger.service';

@Component({
  selector: 'app-image-cropper',
  templateUrl: './image-cropper.component.html',
  styleUrls: ['./image-cropper.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => ImageCropperComponent),
      multi: true,
    },
  ],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class ImageCropperComponent implements ControlValueAccessor {
  @Input() width: number;
  @Input() height: number;
  @Input() formControlName: string;
  @Input() formGroup: FormGroup;
  @Input() defaultImageUrl: string;

  imageUrl: any;
  imageChangedEvent: any = '';
  croppedImage: any = '';
  cropperOpen: boolean = false;

  private onChange: (value: any) => void = () => {};
  private onTouched: () => void = () => {};

  constructor(
    private cdr: ChangeDetectorRef,
    private logger: LoggerService,
  ) {}

  ngOnInit(): void {
    this.imageUrl = this.defaultImageUrl;
    const formControl = this.formGroup.get([this.formControlName]);
    if (formControl && formControl.value) {
      this.imageUrl = formControl.value;
    }
  }

  writeValue(value: any): void {
    this.logger.debug('ImageCropperComponent writeValue', value);
    this.croppedImage = value;

    if (value) {
      // If value is provided and is a Blob or File, use it to create the URL
      this.imageUrl =
        value instanceof Blob || value instanceof File
          ? URL.createObjectURL(value)
          : value;
    } else {
      // If value is empty, use the default image
      this.imageUrl = this.defaultImageUrl;
    }
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  fileChangeEvent(event: any): void {
    if (event.target.files && event.target.files[0]) {
      const file: File = event.target.files[0];
      const reader = new FileReader();

      reader.onload = () => {
        this.imageChangedEvent = event;
        this.imageUrl = reader.result as string; // Display the selected image

        // Immediately populate the form control with the base64 string
        this.croppedImage = this.imageUrl;
        this.formGroup.get([this.formControlName])?.setValue(this.croppedImage);
        this.onChange(this.croppedImage); // Notify ControlValueAccessor
        this.onTouched(); // Mark the control as touched
        this.cdr.detectChanges();
      };

      reader.readAsDataURL(file); // Convert the file to base64
    }
  }

  imageCropped(event: any): void {
    const blob = event.blob;

    const reader = new FileReader();
    reader.onloadend = () => {
      this.croppedImage = reader.result; // This will be a base64 string
      this.onChange(this.croppedImage); // Send the base64 data to the form control
    };
    reader.readAsDataURL(blob); // Converts Blob to base64
  }

  imageLoaded(event: any): void {
    // console.log('Image loaded', event);
    // Callback when image is loaded
  }

  cropperReady(): void {
    // Callback when cropper is ready
  }

  loadImageFailed(): void {
    // Callback when image load fails
  }

  openCropper(): void {
    this.cropperOpen = true;
  }

  closeCropper(reason: 'close' | 'save'): void {
    this.cropperOpen = false;
    if (reason === 'save') {
      this.imageUrl = this.croppedImage;
      this.onChange(this.croppedImage);
      this.onTouched();
    }
  }

  removeImage(): void {
    this.imageUrl = this.defaultImageUrl;
    this.croppedImage = '';
    this.onChange(null); // Reset the control
  }
}
